import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, PO_STATUSES, APPROVAL_STATUSES } from '../api.js';
import { useResizableColumns } from '../components/useResizableColumns.jsx';

// Column ids must line up positionally with the <th>/<td> below.
// `sort` is the server-side field name (see SORTABLE in routes/purchaseOrders.js);
// a column without one is not sortable — concessions are M:N, so there is no
// single value to order on.
const PO_COLUMNS = [
  { id: 'number', width: 84, min: 64, sort: 'number' },
  { id: 'date', width: 116, min: 90, sort: 'orderedAt' },
  { id: 'concession', width: 150, min: 80 },
  { id: 'item', width: 260, min: 100, sort: 'itemName' },
  { id: 'vendor', width: 170, min: 90, sort: 'vendor' },
  { id: 'amountBefore', width: 140, min: 90, sort: 'amountBeforeTax', numeric: true },
  { id: 'amountAfter', width: 140, min: 90, sort: 'amountAfterTax', numeric: true },
  { id: 'status', width: 130, min: 90, sort: 'status' },
  { id: 'approval', width: 140, min: 90, sort: 'approvalStatus' },
];

// Text reads naturally A→Z first; numbers, dates and workflow states are most
// useful largest/newest first.
const TEXT_SORTS = new Set(['itemName', 'vendor']);

const EMPTY_FILTERS = {
  status: '', approval: '', q: '', concession: '', department: '', currency: '', offBudget: false,
};

function formatMoney(amount, currency, opts = {}) {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency', currency: currency || 'CAD',
    maximumFractionDigits: opts.compact ? 0 : 2,
  }).format(Number(amount));
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA');
}

const statusBadge = (status) => {
  const colors = {
    DRAFT: 'bg-zinc-800 text-zinc-400',
    ORDERED: 'bg-blue-900/30 text-blue-300 border-blue-800',
    RECEIVED: 'bg-emerald-900/30 text-emerald-300 border-emerald-800',
    CANCELLED: 'bg-zinc-900 text-zinc-500',
  }[status] || 'bg-zinc-800 text-zinc-400';
  return `inline-flex items-center rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-zinc-800 ${colors}`;
};

const approvalBadge = (s) => {
  const colors = {
    AUTO_APPROVED: 'bg-zinc-900 text-zinc-500',
    PENDING: 'bg-amber-900/30 text-amber-300 border-amber-800',
    APPROVED: 'bg-emerald-900/30 text-emerald-300 border-emerald-800',
    REJECTED: 'bg-rose-900/30 text-rose-300 border-rose-800',
  }[s] || 'bg-zinc-800 text-zinc-400';
  return `inline-flex items-center rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-zinc-800 ${colors}`;
};

// Chevron shown in a sortable header: dimmed + neutral until the column is the
// active one, so every sortable column advertises itself without shouting.
function SortIcon({ state }) {
  if (!state) {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
           className="shrink-0 opacity-0 group-hover:opacity-40 transition-opacity">
        <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
      </svg>
    );
  }
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         className="shrink-0 text-cyan-400">
      {state === 'asc' ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}
    </svg>
  );
}

function StatCard({ label, value, sublabel, accent = false }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-cyan-400' : 'text-zinc-100'}`}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1">{sublabel}</div>}
    </div>
  );
}

export default function POList() {
  const { t } = useTranslation();
  const { totalWidth, colGroup, resizer } = useResizableColumns('po', PO_COLUMNS, {
    resizerTitle: t('common.resizeHint'),
  });
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [concessions, setConcessions] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Reference lists for the two dropdowns. Fetched independently on purpose: a
  // shared Promise.all with one catch once blanked the whole vendor dropdown when
  // a single call failed, so here one failing list can't take the other down.
  useEffect(() => {
    let active = true;
    api.get('/api/concessions')
      .then((res) => { if (active) setConcessions(res.data.concessions); })
      .catch((err) => console.error('[PO filters] concessions:', err.message));
    api.get('/api/departments')
      .then((res) => { if (active) setDepartments(res.data.departments); })
      .catch((err) => console.error('[PO filters] departments:', err.message));
    return () => { active = false; };
  }, []);
  // Sorting is server-side: the list is capped at 100 rows, so sorting only the
  // loaded page would silently reorder a slice and look like the whole set.
  const [sort, setSort] = useState({ key: 'orderedAt', dir: 'desc' });

  const toggleSort = (key) => {
    setSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: TEXT_SORTS.has(key) ? 'asc' : 'desc' }
    ));
  };

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    if (filters.status) qs.set('status', filters.status);
    if (filters.approval) qs.set('approval', filters.approval);
    if (filters.q) qs.set('q', filters.q);
    if (filters.concession) qs.set('concession', filters.concession);
    if (filters.department) qs.set('department', filters.department);
    if (filters.currency) qs.set('currency', filters.currency);
    if (filters.offBudget) qs.set('offBudget', 'true');
    qs.set('sort', sort.key);
    qs.set('dir', sort.dir);
    return qs.toString();
  }, [filters, sort]);

  // Drives the "clear" button; `offBudget` is a boolean so a plain truthiness
  // check over the values covers every filter without listing them again.
  const hasFilters = Object.values(filters).some(Boolean);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/api/purchase-orders?${queryString}`)
      .then((res) => { if (active) setPos(res.data.purchaseOrders); })
      .catch(() => { if (active) setPos([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [queryString]);

  const stats = useMemo(() => {
    let totalCAD = 0;
    let totalUSD = 0;
    let offBudget = 0;
    let pending = 0;
    let received = 0;
    for (const po of pos) {
      if (po.amountAfterTax != null && !po.amountUnknown) {
        const amt = Number(po.amountAfterTax);
        if (po.currency === 'USD') totalUSD += amt; else totalCAD += amt;
        if (po.isOffBudget) offBudget += amt;
      }
      if (po.approvalStatus === 'PENDING') pending += 1;
      if (po.status === 'RECEIVED') received += 1;
    }
    return { totalCAD, totalUSD, offBudget, pending, received };
  }, [pos]);

  return (
    <div className="px-8 py-6 space-y-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('po.title')}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{t('po.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/api/purchase-orders/export.xlsx?${queryString}`}
            className="btn-ghost border border-zinc-800"
            title={t('po.exportXlsx')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t('po.exportXlsx')}
          </a>
          <a
            href={`/api/purchase-orders/export.csv?${queryString}`}
            className="btn-ghost border border-zinc-800"
            title="CSV"
          >
            {t('po.exportCsv')}
          </a>
          <Link to="/purchase-orders/new" className="btn-primary">{t('po.new')}</Link>
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={t('po.stat.total')}
          value={formatMoney(stats.totalCAD, 'CAD', { compact: true })}
          sublabel={stats.totalUSD > 0 ? `+ ${formatMoney(stats.totalUSD, 'USD', { compact: true })}` : null}
          accent
        />
        <StatCard
          label={t('po.stat.pendingApproval')}
          value={stats.pending}
          sublabel={pos.length > 0 ? t('po.count', { count: pos.length }) : null}
        />
        <StatCard
          label={t('po.stat.received')}
          value={stats.received}
        />
        <StatCard
          label={t('po.stat.offBudget')}
          value={formatMoney(stats.offBudget, 'CAD', { compact: true })}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          placeholder={t('po.search')}
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          className="input max-w-xs"
        />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="input max-w-[160px]">
          <option value="">{t('po.allStatuses')}</option>
          {PO_STATUSES.map((s) => <option key={s} value={s}>{t(`po.status.${s}`)}</option>)}
        </select>
        <select value={filters.approval} onChange={(e) => setFilters({ ...filters, approval: e.target.value })} className="input max-w-[220px]">
          <option value="">{t('po.allApprovals')}</option>
          {APPROVAL_STATUSES.map((s) => <option key={s} value={s}>{t(`po.approval.${s}`)}</option>)}
        </select>
        <select value={filters.concession} onChange={(e) => setFilters({ ...filters, concession: e.target.value })} className="input max-w-[200px]">
          <option value="">{t('po.allConcessions')}</option>
          {concessions.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
        </select>
        <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })} className="input max-w-[180px]">
          <option value="">{t('po.allDepartments')}</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filters.currency} onChange={(e) => setFilters({ ...filters, currency: e.target.value })} className="input max-w-[130px]">
          <option value="">{t('po.allCurrencies')}</option>
          <option value="CAD">CAD</option>
          <option value="USD">USD</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer select-none whitespace-nowrap">
          <input
            type="checkbox"
            checked={filters.offBudget}
            onChange={(e) => setFilters({ ...filters, offBudget: e.target.checked })}
            className="accent-cyan-500"
          />
          {t('po.offBudgetOnly')}
        </label>
        {hasFilters && (
          <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="btn-ghost text-xs">
            {t('po.clearFilters')}
          </button>
        )}
        <div className="ml-auto text-xs text-zinc-500 tabular-nums">
          {pos.length} {pos.length === 1 ? 'PO' : 'POs'}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="text-sm table-fixed" style={{ width: totalWidth, minWidth: '100%' }}>
          {colGroup}
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              {PO_COLUMNS.map((col, i) => {
                const dir = col.sort && sort.key === col.sort ? sort.dir : null;
                return (
                  <th
                    key={col.id}
                    aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`relative px-4 py-2.5 ${col.numeric ? 'text-right' : 'text-left'}`}
                  >
                    {col.sort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.sort)}
                        title={t('po.sortHint')}
                        className={`group flex w-full items-center gap-1 pr-2 transition-colors hover:text-zinc-200
                                    ${col.numeric ? 'justify-end' : ''} ${dir ? 'text-zinc-200' : ''}`}
                      >
                        <span className="truncate">{t(`po.col.${col.id}`)}</span>
                        <SortIcon state={dir} />
                      </button>
                    ) : (
                      <span className="block truncate pr-2">{t(`po.col.${col.id}`)}</span>
                    )}
                    {i < PO_COLUMNS.length - 1 && resizer(i)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={PO_COLUMNS.length} className="px-4 py-12 text-center text-zinc-500">{t('common.loading')}</td></tr>
            )}
            {!loading && pos.length === 0 && (
              <tr><td colSpan={PO_COLUMNS.length} className="px-4 py-12 text-center text-zinc-500">{t('po.empty')}</td></tr>
            )}
            {pos.map((po) => (
              <tr key={po.id} className="border-t border-zinc-800 hover:bg-zinc-900/60 transition-colors">
                <td className="px-4 py-3">
                  <Link to={`/purchase-orders/${po.id}`} className="text-cyan-400 hover:underline font-medium tabular-nums">#{po.number}</Link>
                </td>
                <td className="px-4 py-3 text-zinc-400 tabular-nums truncate">{formatDate(po.orderedAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {po.concessions.map((c) => (
                      <span key={c.id} className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] bg-zinc-800 text-zinc-300">
                        {c.code}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 truncate" title={po.itemName}>{po.itemName}</td>
                <td className="px-4 py-3 text-zinc-400 truncate" title={po.vendor}>{po.vendor}</td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                  {po.amountUnknown
                    ? <span className="text-amber-400">???</span>
                    : formatMoney(po.amountBeforeTax, po.currency)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {po.amountUnknown
                    ? <span className="text-amber-400">???</span>
                    : <>{formatMoney(po.amountAfterTax, po.currency)}
                        <span className="ml-1 text-[10px] text-zinc-500">{po.currency}</span></>}
                </td>
                <td className="px-4 py-3"><span className={statusBadge(po.status)}>{t(`po.status.${po.status}`)}</span></td>
                <td className="px-4 py-3"><span className={approvalBadge(po.approvalStatus)}>{t(`po.approval.${po.approvalStatus}`)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
